import { createFileRoute, useRouter } from "@tanstack/react-router";
import CountUp from "@/components/CountUp";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MyTicketsSheet from "@/routes/(game)/lucky-draw/-components/MyTicketsSheet";
import WinnersSheet from "@/routes/(game)/lucky-draw/-components/WinnersSheet";
import { LuckyDrawSparkles } from "@/routes/(game)/lucky-draw/-components/LuckyDrawSparkles";
import { Panel } from "@/routes/(game)/lucky-draw/-components/Panel";
import { PrizeDistributionDialog } from "@/routes/(game)/lucky-draw/-components/PrizeDistributionDialog";
import { usePrivy } from "@privy-io/react-auth";
import {
  useLuckyDrawLeaderboard,
  useLuckyDrawParticipate,
  useLuckyDrawPrizes,
  useLuckyDrawTickets,
  useStats,
} from "@/lib/hooks/useApi";
import { toast } from "sonner";
import { Countdown } from "@/components/ui/countdown";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { DefaultButton } from "@/components/DefaultButton";
import AnimatedLoading from "@/components/AnimatedLoading";
import { ShinyText } from "@/components/ShinyText";
import { WinnersCarousel } from "@/routes/(game)/lucky-draw/-components/WinnersCarousel";
import ResourceBar from "@/components/ResourceBar";
import { HowItWorkButton } from "@/components/ui/HowItWorkButton";
import useUserAgent from "@/lib/hooks/useUserAgent";
import { LuckyDrawHistory } from "./-components/History";
import { DailyTrophyBonusButton } from "@/components/DailyTrophyBonusButton";
import { LoginGate } from "@/components/LoginGate";
import { t } from "@/lib/i18n";
import { useSetAtom } from "jotai";
import { missionWidgetOpenAtom } from "@/lib/missionWidgetAtom";
import { useNextStep } from "@/components/NextStepProvider";
import {
  trackLuckyDrawHistoryOpenClick,
  trackLuckyDrawLeaderboardOpen,
  trackLuckyDrawParticipateSuccess,
} from "@/lib/analytics/lucky-draw";
import { LeaderboardSheet } from "@/routes/(game)/lucky-draw/-components/LeaderboardSheet";
import { generateTextShadow } from "@/lib/utils";
import { InfoIconPixel } from "@/routes/(game)/lucky-draw/-components/InfoIconPixel";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export const Route = createFileRoute("/(game)/lucky-draw/")({
  component: RouteComponent,
});

const toUnixSeconds = (value?: string | number | null) => {
  if (value == null) return 0;
  if (typeof value === "number") return value;
  if (/^\d+$/.test(value)) {
    const numeric = Number(value);
    return numeric > 1_000_000_000_000 ? Math.floor(numeric / 1000) : numeric;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : Math.floor(parsed / 1000);
};

function RouteComponent() {
  const { user } = usePrivy();
  const walletAddress = user?.wallet?.address;
  const router = useRouter();
  const { isMobile } = useUserAgent();
  const setMissionWidgetOpen = useSetAtom(missionWidgetOpenAtom);
  const { startNextStep, closeNextStep } = useNextStep();

  const handleStartLuckyDrawTour = useCallback(() => {
    try {
      closeNextStep();
      // eslint-disable-next-line no-empty
    } catch {}
    startNextStep("luckyDrawTour");
  }, [closeNextStep, startNextStep]);

  const [ticketCodes, setTicketCodes] = useState<string[]>([]);
  const [isTicketsSheetOpen, setIsTicketsSheetOpen] = useState(false);
  const { data: statsData, refetch: refetchStats } = useStats(
    walletAddress ?? "",
  );
  const gameWalletAddress = statsData?.embeddedWallet;
  const userId = statsData?.userId;
  const {
    data: prizesData,
    isLoading,
    //isFetching,
    status,
    refetch: refetchPrizes,
    dataUpdatedAt: prizesUpdatedAt,
  } = useLuckyDrawPrizes(userId);
  const {
    data: ticketsData,
    isLoading: isTicketsLoading,
    isFetching: isTicketsFetching,
    refetch: refetchTickets,
  } = useLuckyDrawTickets(isTicketsSheetOpen ? userId : undefined);
  const { mutateAsync: participate, isPending } = useLuckyDrawParticipate();
  const {
    data: leaderboardPages,
    isLoading: isLeaderboardLoading,
    refetch: refetchLeaderboard,
    fetchNextPage: fetchNextLeaderboardPage,
    hasNextPage: hasNextLeaderboardPage,
    isFetchingNextPage: isFetchingNextLeaderboardPage,
  } = useLuckyDrawLeaderboard(userId);
  const leaderboardFirstPage = leaderboardPages?.pages[0];
  const isCampaignLive = Boolean(prizesData?.luckyDraw);
  const luckyDrawStatus = prizesData?.luckyDraw?.status;
  // const isLuckyDrawWaiting = luckyDrawStatus === "waiting";
  const isLuckyDrawLive = luckyDrawStatus === "live";
  const isLuckyDrawDistributed = luckyDrawStatus === "distributed";

  const participantsCount = prizesData?.participantsCount ?? 0;
  const userEntries = prizesData?.userEntries ?? ticketsData?.ticketCount ?? 0;
  const availableTicketsCount =
    ticketsData?.luckyDrawTicket ?? statsData?.luckyDrawTicket ?? 0;
  const fetchedTickets = useMemo(
    () => (ticketsData?.tickets ?? []).map((ticket) => ticket.ticketCode),
    [ticketsData?.tickets],
  );
  const normalizedTickets = useMemo(() => {
    const combined = [...fetchedTickets, ...ticketCodes];
    const seen = new Set<string>();
    return combined.filter((ticket) => {
      if (!ticket || seen.has(ticket)) return false;
      seen.add(ticket);
      return true;
    });
  }, [fetchedTickets, ticketCodes]);

  const endTimestamp = useMemo(
    () => toUnixSeconds(prizesData?.luckyDraw?.endDate),
    [prizesData?.luckyDraw?.endDate],
  );

  // nextLuckyDraw is always null now — next draw opens at daily cutover
  const nextTimestamp = 0;

  const totalPrizeUsd =
    Math.round(
      (leaderboardFirstPage?.estimatedPool?.usd ??
        prizesData?.estimatedPool?.usd ??
        prizesData?.luckyDraw?.totalPrizeInUsd ??
        0) * 100,
    ) / 100;
  const prevPrizeRef = useRef(totalPrizeUsd);
  const [prizeGlitch, setPrizeGlitch] = useState(false);
  const [prizeCountUp, setPrizeCountUp] = useState(false);
  const countUpFromRef = useRef(totalPrizeUsd);
  const glitchTimerRef = useRef<number>(0);
  const countUpTimerRef = useRef<number>(0);
  const prevPrizesUpdatedAt = useRef(prizesUpdatedAt);
  useEffect(() => {
    if (!isLuckyDrawLive || prizesUpdatedAt === prevPrizesUpdatedAt.current) return;
    prevPrizesUpdatedAt.current = prizesUpdatedAt;
    const changed = totalPrizeUsd !== prevPrizeRef.current;
    if (changed) {
      countUpFromRef.current = prevPrizeRef.current;
      prevPrizeRef.current = totalPrizeUsd;
      // Clear any pending glitch
      window.clearTimeout(glitchTimerRef.current);
      setPrizeGlitch(false);
      // Always clear previous countup timer before setting new one
      window.clearTimeout(countUpTimerRef.current);
      setPrizeCountUp(true);
      countUpTimerRef.current = window.setTimeout(
        () => setPrizeCountUp(false),
        600,
      );
    } else {
      prevPrizeRef.current = totalPrizeUsd;
      // Clear any pending countup
      window.clearTimeout(countUpTimerRef.current);
      setPrizeCountUp(false);
      // Always clear previous glitch timer before setting new one
      window.clearTimeout(glitchTimerRef.current);
      setPrizeGlitch(true);
      glitchTimerRef.current = window.setTimeout(
        () => setPrizeGlitch(false),
        300,
      );
    }
  }, [prizesUpdatedAt, isLuckyDrawLive, totalPrizeUsd]);
  const winners = prizesData?.winners ?? [];
  const shouldShowWinners = isLuckyDrawDistributed;
  const [showWinnersPanel, setShowWinnersPanel] = useState(shouldShowWinners);
  const [isWinnersTransition, setIsWinnersTransition] = useState(false);
  const shouldShowWinnersRef = useRef(shouldShowWinners);

  useEffect(() => {
    const intervalMs = 3_000;
    const interval = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      void refetchPrizes();
      void refetchStats();
    }, intervalMs);

    return () => window.clearInterval(interval);
  }, [refetchPrizes, refetchStats]);
  const [isLeaderboardSheetOpen, setIsLeaderboardSheetOpen] = useState(false);
  useEffect(() => {
    if (!isLeaderboardSheetOpen) return;
    const intervalMs = 1_500;
    const interval = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      void refetchLeaderboard();
    }, intervalMs);

    return () => window.clearInterval(interval);
  }, [isLeaderboardSheetOpen, refetchLeaderboard]);
  useEffect(() => {
    const wasShowingWinners = shouldShowWinnersRef.current;

    if (!wasShowingWinners && shouldShowWinners) {
      setIsWinnersTransition(true);
      setShowWinnersPanel(false);
      const timeoutId = window.setTimeout(() => {
        setShowWinnersPanel(true);
        setIsWinnersTransition(false);
      }, 320);
      shouldShowWinnersRef.current = shouldShowWinners;
      return () => window.clearTimeout(timeoutId);
    }

    setShowWinnersPanel(shouldShowWinners);
    setIsWinnersTransition(false);
    shouldShowWinnersRef.current = shouldShowWinners;
  }, [shouldShowWinners]);
  const sortedWinners = useMemo(
    () => [...winners].sort((a, b) => a.prizeOrder - b.prizeOrder),
    [winners],
  );
  const winningTicketCodes = useMemo(() => {
    if (!gameWalletAddress) return [];
    return winners
      .filter((winner) => winner.address === gameWalletAddress)
      .map((winner) => winner.winnerTicketCode);
  }, [winners, gameWalletAddress]);

  const handleConversionComplete = () => {
    if (isTicketsSheetOpen) {
      void refetchTickets();
    }
    void refetchStats();
  };

  const handleHistoryOpen = () => {
    trackLuckyDrawHistoryOpenClick({ source: "lucky_draw_main" });
    router.navigate({ to: "/lucky-draw/history" });
  };

  const handleParticipate = async (ticketCount: number) => {
    if (!walletAddress) {
      toast.error(t("luckyDraw.walletRequired"), {
        description: t("luckyDraw.connectWalletToEnter"),
      });
      return;
    }

    try {
      const response = await participate({ ticketCount });
      trackLuckyDrawParticipateSuccess({
        lucky_draw_id:
          response?.luckyDrawId ?? prizesData?.luckyDraw?.id ?? "unknown",
        ticket_count: ticketCount,
        ticket_codes_count: response?.ticketCodes?.length ?? 0,
      });

      if (response?.ticketCodes?.length) {
        setTicketCodes((prev) => [...prev, ...response.ticketCodes]);
      }
      if (isTicketsSheetOpen) {
        await refetchTickets();
      }
      await refetchStats();
      toast.success(t("luckyDraw.ticketsAdded"));
    } catch (error) {
      console.error(error);
      toast.error(t("luckyDraw.enterDrawFailed"), {
        description: t("luckyDraw.tryAgainSoon"),
      });
    }
  };

  if (isLoading && !prizesData) {
    return <AnimatedLoading />;
  }

  if (status === "success" && !isCampaignLive) {
    return (
      <div
        className="relative flex min-h-svh flex-col gap-6 px-2 py-6"
        style={{
          backgroundImage:
            "url(https://cdn.navalabs.io/components/lucky-draw/bg-lucky-draw_m8vkrp.png)",
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          imageRendering: "pixelated",
        }}
      >
        <FloatingLeftButton
          onClick={() => router.navigate({ to: "/home" })}
          className="absolute left-0 top-4 z-20"
          icon={
            <svg
              className="w-4 h-4"
              viewBox="0 0 9 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                fill="white"
              />
            </svg>
          }
        />
        <ResourceBar
          showLuckyDrawTicket
          showBalance={false}
          availableTicketsCount={availableTicketsCount}
          luckyDrawTicketCount={availableTicketsCount}
          handleConversionComplete={handleConversionComplete}
          className="absolute top-4 left-14 right-4 flex z-20 pl-3"
          rightExtra={
            <HowItWorkButton
              onClick={handleStartLuckyDrawTour}
              size={"size-8!"}
            />
          }
        />
        <div className="flex-1 flex flex-col gap-4 items-center justify-center px-2 py-6">
          <h6 className="text-2xl uppercase">
            <ShinyText text={t("luckyDraw.title")} dy={5} />
          </h6>
          <Panel
            style={{
              animation: "lucky-float-in 420ms ease-out 40ms both",
            }}
          >
            <h3 className="mt-2 max-w-md text-xs text-[#f9e0a6]/90 text-center mx-2">
              {t("luckyDraw.introDescription")}
            </h3>
          </Panel>
          <div className="flex items-center justify-center gap-2 black-container h-8 px-1 opacity-75">
            <img
              src="https://cdn.navalabs.io/icons/timer_rqnxy6.png"
              className="w-3.5"
              alt={t("luckyDraw.timerAlt")}
            />
            <Countdown startTimestamp={1770940800} />
          </div>
        </div>

        <div className="pb-8 sm:pb-10 w-full h-20 z-10 flex flex-col items-center justify-center gap-4">
          <div className="w-4/5 h-12 flex gap-6 items-center justify-center">
            <div data-step="lucky-draw-earn" className="flex-1 h-full">
              <LoginGate
                unauthenticatedLabel={<p>{t("luckyDraw.earnLuckyTicket")}</p>}
              >
                <DefaultButton
                  className="w-full h-full uppercase drop-shadow-lg"
                  onClick={() => setMissionWidgetOpen(true)}
                >
                  {t("luckyDraw.earnLuckyTicket")}
                </DefaultButton>
              </LoginGate>
            </div>
            <div data-step="lucky-draw-tickets" className="flex-1 h-full">
              <MyTicketsSheet
                entries={normalizedTickets}
                userEntries={userEntries}
                winningTicketCodes={winningTicketCodes}
                onParticipate={handleParticipate}
                isSubmitting={isPending}
                isTicketsLoading={isTicketsLoading || isTicketsFetching}
                availableTicketsCount={availableTicketsCount}
                isLuckyDrawEnded={true}
                isLuckyDrawNotStarted={true}
                onOpenChange={setIsTicketsSheetOpen}
              >
                <LoginGate
                  unauthenticatedLabel={
                    <DefaultButton className="w-full h-full uppercase drop-shadow-lg">
                      {t("luckyDraw.myLuckyTicket")}
                    </DefaultButton>
                  }
                >
                  <DefaultButton className="w-full h-full uppercase drop-shadow-lg">
                    {t("luckyDraw.myLuckyTicket")}
                  </DefaultButton>
                </LoginGate>
              </MyTicketsSheet>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative flex min-h-svh flex-col gap-6"
      style={{
        backgroundImage:
          "url(https://cdn.navalabs.io/components/lucky-draw/bg-lucky-draw_m8vkrp.png)",
        backgroundSize: "100% 100%",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        imageRendering: "pixelated",
      }}
    >
      <style>{`
        @keyframes prize-glitch {
          0% { transform: translateX(0) scale(1); opacity: 1; }
          15% { transform: translateX(-3px) scale(1.03); opacity: 0.5; }
          30% { transform: translateX(3px) scale(1.03); opacity: 0.5; }
          45% { transform: translateX(-2px) scale(1.02); opacity: 0.6; }
          60% { transform: translateX(2px) scale(1.01); opacity: 0.7; }
          75% { transform: translateX(-1px) scale(1); opacity: 0.85; }
          100% { transform: translateX(0) scale(1); opacity: 1; }
        }

        @keyframes lucky-pop {
          0% {
            transform: scale(0.2) rotate(-12deg);
            opacity: 0;
          }
          60% {
            transform: scale(1.18) rotate(6deg);
            opacity: 1;
          }
          80% {
            transform: scale(0.95) rotate(-2deg);
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }

        @keyframes lucky-float-in {
          0% {
            transform: translateY(12px) scale(0.98);
            opacity: 0;
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }

        @keyframes lucky-pop-out {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          60% {
            transform: scale(1.12);
            opacity: 1;
          }
          100% {
            transform: scale(0.9);
            opacity: 0;
          }
        }

        @keyframes lucky-winner-card-in {
          0% {
            transform: translateY(8px) scale(0.96);
            opacity: 0;
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }

        @keyframes lucky-sparkle {
          0% {
            transform: translate(-50%, -50%) scale(0.2) rotate(0deg);
            opacity: 0;
          }
          35% {
            opacity: 1;
          }
          100% {
            transform: translate(
                calc(-50% + var(--x)),
                calc(-50% + var(--y))
              )
              scale(1.2)
              rotate(30deg);
            opacity: 0;
          }
        }
      `}</style>
      <img
        src={
          "https://cdn.navalabs.io/components/lucky-draw/lucky-draw-light_p2lsdf.png"
        }
        alt={t("luckyDraw.lightAlt")}
        className="absolute left-0 top-0 w-full h-full"
      />
      <FloatingLeftButton
        onClick={() => router.navigate({ to: "/home" })}
        className="absolute left-0 top-4 z-20"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />
      <ResourceBar
        showBalance={false}
        showLuckyDrawTicket
        availableTicketsCount={availableTicketsCount}
        luckyDrawTicketCount={availableTicketsCount}
        handleConversionComplete={handleConversionComplete}
        className="absolute top-4 left-14 right-4 flex z-20 pl-3"
        rightExtra={
          <HowItWorkButton onClick={handleStartLuckyDrawTour} size={"size-8"} />
        }
      />
      <div className="absolute top-20 right-4 z-50 flex flex-col items-center gap-3">
        <LuckyDrawHistory
          onClickAction={() => router.navigate({ to: `/lucky-draw/history` })}
          className={isMobile ? "size-7" : "size-8"}
        />
        {!!user && <DailyTrophyBonusButton source="lucky-draw" />}
      </div>
      <div className="z-10 relative flex-1 flex flex-col items-center justify-end">
        <img
          src={
            "https://cdn.navalabs.io/components/lucky-draw/lucky-draw-stand_r7kqmx.png"
          }
          alt={t("luckyDraw.standsAlt")}
          className="absolute w-full h-auto left-0 bottom-0"
        />
        <div className="relative z-9 w-full flex-1 flex flex-col items-center justify-start pt-14 gap-2">
          <div className="relative w-2/5" data-step="lucky-draw-prize">
            <Panel className="flex flex-col items-center" shine>
              <div className="relative flex gap-2 items-center">
                <h6 className="text-[0.8rem] text-[#f9e0a6]/90 uppercase mt-1 text-nowrap">
                  {t("luckyDraw.todayPrizePool")}
                </h6>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="z-10 hover:cursor-pointer hover:brightness-95 active:scale-98"
                    >
                      <InfoIconPixel className="h-3 w-auto text-[#f9e0a6]/60" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="max-w-xs text-sm text-left">
                    {t("luckyDraw.prizePoolInfo")}
                  </PopoverContent>
                </Popover>
              </div>
              <PrizeDistributionDialog prizes={prizesData?.prizes ?? []}>
                <button
                  type="button"
                  className={
                    prizeGlitch
                      ? "mt-1 hover:cursor-pointer hover:brightness-95 active:scale-98 animate-[prize-glitch_300ms_ease-out]"
                      : "mt-1 hover:cursor-pointer hover:brightness-95 active:scale-98"
                  }
                >
                  {prizeCountUp ? (
                    <div className="flex items-baseline">
                      <span className="text-3xl sm:text-4xl font-bold text-[#f9e0a6]">
                        $
                      </span>
                      <CountUp
                        from={countUpFromRef.current}
                        to={totalPrizeUsd}
                        className="text-3xl sm:text-4xl font-bold text-[#f9e0a6] leading-none"
                        duration={0.5}
                      />
                    </div>
                  ) : (
                    <ShinyText
                      text={`$${totalPrizeUsd}`}
                      className="text-3xl sm:text-4xl"
                    />
                  )}
                </button>
              </PrizeDistributionDialog>
            </Panel>
          </div>
          <Panel className="px-3 py-1.5! min-w-52 uppercase tracking-[0.12em] text-right flex items-center justify-between gap-4">
            <div className="text-[0.75rem] text-[#f9e0a6]/70">
              {t("luckyDraw.totalParticipants")}
            </div>
            <CountUp
              to={participantsCount}
              className="text-[0.75rem] leading-none"
              duration={0.5}
            />
          </Panel>
          {isLuckyDrawLive || nextTimestamp > 0 ? (
            <div
              className={`${isLuckyDrawLive ? null : "mt-2"} relative flex flex-col items-center justify-center`}
            >
              {isLuckyDrawLive ? null : (
                <p className="absolute z-10 left-1/2 -translate-x-1/2 -top-3.5 text-nowrap uppercase text-shadow-black">
                  {t("luckyDraw.nextDrawIn")}
                </p>
              )}
              <div
                {...(isLuckyDrawLive
                  ? { "data-step": "lucky-draw-schedule" }
                  : {})}
                className="flex items-center justify-center gap-2 black-container h-8 px-1 opacity-75"
              >
                <img
                  src="https://cdn.navalabs.io/icons/timer_rqnxy6.png"
                  className="w-3.5"
                  alt={t("luckyDraw.timerAlt")}
                />
                <Countdown
                  startTimestamp={
                    isLuckyDrawLive ? endTimestamp : nextTimestamp
                  }
                />
              </div>
            </div>
          ) : (
            <p className="mt-2 text-center text-xs uppercase text-shadow-black">
              {t("luckyDraw.noNextDraw")}
            </p>
          )}
        </div>
        <div className="relative z-15 w-full h-1/2 flex flex-col items-center justify-end">
          {(isWinnersTransition || showWinnersPanel) && (
            <LuckyDrawSparkles className="absolute -top-32 sm:-top-40 left-1/2 w-[22rem] h-[22rem] -translate-x-1/2 z-4" />
          )}
          {!showWinnersPanel ? (
            <>
              <div
                data-step="lucky-draw-box"
                className="absolute -top-20 sm:-top-24 left-1/2 -translate-x-1/2 w-2/3 z-5 flex flex-col items-center"
              >
                <div className="flex flex-col items-center mb-1 animate-[vip-bounce_1.5s_ease-in-out_infinite]">
                  <span
                    className="text-sm font-black text-white uppercase tracking-wider text-nowrap"
                    style={{ textShadow: generateTextShadow(0.5) }}
                  >
                    {t("luckyDraw.leaderboard")}
                  </span>
                  <img
                    src="https://cdn.navalabs.io/icons/pin_mnldpc.svg"
                    alt=""
                    className="w-6 h-auto"
                  />
                </div>
                <LeaderboardSheet
                  pages={leaderboardPages?.pages}
                  prizes={prizesData?.prizes ?? []}
                  isLoading={isLeaderboardLoading}
                  fetchNextPage={fetchNextLeaderboardPage}
                  hasNextPage={hasNextLeaderboardPage}
                  isFetchingNextPage={isFetchingNextLeaderboardPage}
                  userStats={statsData}
                  onOpenChange={(open) => {
                    setIsLeaderboardSheetOpen(open);
                    if (open) {
                      void refetchLeaderboard();
                      trackLuckyDrawLeaderboardOpen({
                        source: "lucky_draw_main",
                      });
                    }
                  }}
                >
                  <img
                    src={
                      "https://cdn.navalabs.io/components/lucky-draw/lucky-draw-box_z4tqhn.png"
                    }
                    alt={t("luckyDraw.standsAlt")}
                    className="w-full h-auto flex flex-col items-center justify-start hover:cursor-pointer hover:brightness-95 hover:saturate-95 active:scale-98"
                    style={{
                      animation: isWinnersTransition
                        ? "lucky-pop-out 320ms ease-in forwards"
                        : "lucky-pop 720ms cubic-bezier(0.16, 1, 0.3, 1) 0.1s both",
                    }}
                  />
                </LeaderboardSheet>
              </div>
              <Panel className="px-0 absolute top-24 max-w-90 z-5 flex items-center">
                <p className="w-full text-center mx-2 text-xs">
                  {t("luckyDraw.enterWithTickets")} <br />
                  {t("luckyDraw.prizePoolCountdown")}
                </p>
              </Panel>
            </>
          ) : (
            <>
              <div className="absolute -top-24 sm:-top-35 z-5 w-full max-w-76 text-center uppercase tracking-[0.12em]">
                <WinnersCarousel
                  winners={sortedWinners}
                  prizes={prizesData?.prizes}
                />
                <WinnersSheet
                  winners={sortedWinners}
                  prizes={prizesData?.prizes}
                  entries={normalizedTickets}
                  userEntries={userEntries}
                  isTicketsLoading={isTicketsLoading || isTicketsFetching}
                  winningTicketCodes={winningTicketCodes}
                  inventoryOnClickAction={() =>
                    router.navigate({ to: "/inventory" })
                  }
                  onOpenChange={setIsTicketsSheetOpen}
                >
                  <button
                    className="px-4 py-2 text-sm sm:text-base uppercase tracking-[0.15em] text-[#f9e0a6]/80 hover:cursor-pointer hover:brightness-95 hover:saturate-95 active:scale-98"
                    //style={{ textShadow: generateTextShadow(0.5) }}
                  >
                    <ShinyText text={t("luckyDraw.viewWinningTicket")} />
                  </button>
                </WinnersSheet>
              </div>
            </>
          )}
          <img
            src={
              "https://cdn.navalabs.io/components/lucky-draw/lucky-draw-desk_b9xjcw.png"
            }
            alt={t("luckyDraw.standsAlt")}
            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-auto h-full z-4 flex flex-col items-center justify-start"
          />
          <div className="z-10 pb-10 sm:pb-14 w-full h-30 sm:h-24 flex flex-col items-center justify-center gap-4">
            <div className="w-4/5 h-12 flex gap-6 items-center justify-center">
              <div data-step="lucky-draw-earn" className="flex-1 h-full">
                <LoginGate
                  unauthenticatedLabel={<p>{t("luckyDraw.earnLuckyTicket")}</p>}
                >
                  <DefaultButton
                    className="w-full h-full text-nowrap uppercase drop-shadow-lg"
                    onClick={() => setMissionWidgetOpen(true)}
                  >
                    {t("luckyDraw.earnLuckyTicket")}
                  </DefaultButton>
                </LoginGate>
              </div>
              <div data-step="lucky-draw-tickets" className="flex-1 h-full">
                <MyTicketsSheet
                  entries={normalizedTickets}
                  userEntries={userEntries}
                  winningTicketCodes={winningTicketCodes}
                  onParticipate={handleParticipate}
                  isSubmitting={isPending}
                  isTicketsLoading={isTicketsLoading || isTicketsFetching}
                  availableTicketsCount={availableTicketsCount}
                  isLuckyDrawEnded={isLuckyDrawDistributed}
                  onOpenChange={setIsTicketsSheetOpen}
                >
                  <LoginGate
                    unauthenticatedLabel={<p>{t("luckyDraw.myLuckyTicket")}</p>}
                  >
                    <DefaultButton className="w-full h-full uppercase drop-shadow-lg">
                      {t("luckyDraw.myLuckyTicket")}
                    </DefaultButton>
                  </LoginGate>
                </MyTicketsSheet>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
